import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleCategorySelect = (url: string) => {
    setIsOpen(false);
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener nofollow';
    a.click();
  };

  return (
    <section id="categories" className="py-16 md:py-24 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-primary-500 mb-3">
            Przeglądaj
          </span>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
            Najpopularniejsze kategorie Kakobuy Spreadsheet
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Wybierz kategorię z listy poniżej i odkryj najlepsze produkty dostępne w Kakobuy Spreadsheet.
          </p>
        </div>

        {/* Dropdown */}
        <div className="max-w-md mx-auto mb-12 relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between bg-background-50 border border-background-200/70 rounded-lg px-5 py-3.5 text-sm font-medium text-foreground-800 hover:border-primary-300 transition-colors cursor-pointer"
          >
            <span className={selected ? 'text-foreground-950' : 'text-foreground-500'}>
              {selected || 'Wybierz kategorię'}
            </span>
            <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {isOpen && (
            <div className="absolute left-0 right-0 mt-1.5 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-40 max-h-72 overflow-y-auto">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => {
                    setSelected(cat.name);
                    handleCategorySelect(cat.url);
                  }}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors text-left cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-primary-500 text-base w-5 h-5 flex items-center justify-center`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category button grid with images */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
            >
              <div className="w-full aspect-square overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-3 flex items-center gap-2.5">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                  <i className={`${cat.icon} text-sm`}></i>
                </div>
                <span className="text-xs font-medium text-foreground-700 group-hover:text-foreground-950 leading-tight whitespace-nowrap">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}